<template>
  <PageError v-if="error"
   :error="error"
   :errorCode="errorCode"
  />

  <div v-else-if="canViewTunnel || user?.is_staff" class="p-6 dark:text-white">
    <!-- Header Section -->
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
        Tunnels - {{ filteredTunnels.length }}
        <span v-if="isFiltered" class="text-sm text-gray-500">(filtered)</span>
      </h1>
      <div class="flex gap-4">
        <div class="relative group">
        <button
          type="button"
          class="hover:bg-transparent bg-transparent"
          @click="startTutorial"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
        </button>
          <span class="absolute top-full text-xs right-1/2 -translate-y-1/2 mb-1 px-2 py-0.5 glass-card opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
        </div>
        <button v-if="canAddTunnel || user?.is_staff"
          id="add-tunnel-btn"
          @click="goToAddTunnel"
          class="btn bg-blue-700 text-white hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Add Tunnel
        </button>
        <button
          @click="showExportDialog = true"
          class="btn bg-gray-800 text-white hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          Download CSV
        </button>
      </div>
    </div>

    <!-- Organization Tabs -->
    <OrganizationTabs
      v-if="user?.is_staff || isServiceProviderUser"
      :organizations="tenant.organizations.value"
      :selected-org-id="tenant.currentOrganizationId.value"
      :loading="tenant.isLoading.value"
      :show-tabs="true"
      loading-text="Loading tunnels..."
      @select="handleOrgSelect"
    />

    <!-- Search and Filter Section -->
    <div
      class="flex flex-col md:flex-row md:items-center md:justify-between mb-6"
    >
      <!-- Search Bar -->
      <div class="mb-4 md:mb-0 md:flex-1 md:mr-4">
        <SearchBar
          v-model="searchQuery"
          placeholderText="Search tunnels by name..."
        />
      </div>

      <!-- Filters Section -->
      <div
        :class="[
          'md:flex md:items-center',
          showFilters ? 'block' : 'hidden md:block',
        ]"
      >
        <FiltersSection
          :filters="dynamicFilters"
          v-model:selectedFilters="selectedFilters"
          :showFilters="showFilters"
          @update:showFilters="showFilters = $event"
          @applyFilters="applyFilters"
        />
      </div>

      <!-- Bulk Action Section -->
      <div class="flex items-center mt-4 md:mt-0 space-x-2 ml-2">
        <GlassSelect
          v-model="bulkAction"
          placeholder="Bulk Actions"
          :options="canDeleteTunnel || user?.is_staff ? [{ id: 'delete', name: 'Delete selected items' }] : []"
        />
        <button
          @click="applyBulkAction"
          :disabled="!bulkAction || selectedTunnels.length === 0"
          :class="[
            'btn focus:outline-none focus:ring-2 focus:ring-blue-500',
            bulkAction && selectedTunnels.length
              ? 'bg-red-700 text-white hover:bg-red-800'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed',
          ]"
        >
          Apply
        </button>
      </div>
    </div>

    <!-- Tunnels Table -->
    <GenericTable
      id="tunnels-table"
      ref="table"
      :columns="columns"
      :items="paginatedTunnels"
      itemKey="id"
      :selectable="true"
      @update:selectedItems="updateSelectedTunnels"
      @rowClick="goToTunnelDetail"
    >
      <!-- Custom cell templates -->
      <template #site="{ item }">
        {{ getSiteName(item.site) }}
      </template>
      <template #org="{ item }">
        {{ tenant.getOrganizationName(item.org) }}
      </template>
      <template #created_date="{ value }">
        {{ formatDateTime(value) }}
      </template> 
    </GenericTable>

    <!-- Pagination -->
    <div
      class="flex flex-col md:flex-row items-center justify-between mt-8 space-y-4 md:space-y-0"
    >
      <div class="flex items-center space-x-2">
        <button
          @click="changePage(-1)"
          :disabled="currentPage === 1"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Previous
        </button>
        <button
          @click="changePage(1)"
          :disabled="currentPage === totalPages"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Next
        </button>
      </div>
      <div class="text-gray-700 dark:text-white">
        Page {{ currentPage }} of {{ totalPages }}
      </div>
      <div>
        <GlassSelect
          v-model="itemsPerPage"
          @change="updatePagination"
          :options="[{ id: 10, name: '10 per page' }, { id: 20, name: '20 per page' }, { id: 50, name: '50 per page' }, { id: 100, name: '100 per page' }, { id: -1, name: 'All' }]"
        />
      </div>
    </div>
    <TunnelCsvExportDialog
      :is-open="showExportDialog"
      :data="filteredTunnels"
      @close="showExportDialog = false"
    />

    <DeleteModal
      v-if="deletingItem"
      :deleting-item="deletingItem"
      :componet-objects="selectedObjects"
      :is-deleting="isDeleting"
      @delete="confirmDelete"
      @close="deletingItem=false"
      :isChildObject="true"
      :count="count"
    />

  </div>
</template>

<script>
import { getAllTunnels, deleteTunnel } from "@/services/tunnelService";
import GlassSelect from "@/components/GlassSelect.vue";
import { getSites } from "@/services/siteService";
import SearchBar from "@/components/SearchBar.vue";
import FiltersSection from "@/components/FiltersSection.vue";
import GenericTable from "@/components/GenericTable.vue";
import PageError from "@/components/PageError.vue";
import TunnelCsvExportDialog from "@/components/TunnelCsvExportDialog.vue";
import { useStore } from "@/store";
import DeleteModal from "@/components/DeleteModal.vue";
import { useMultiTenant } from "@/composables/useMultiTenant";
import OrganizationTabs from "@/components/OrganizationTabs.vue";
import { useTutorial } from "@/composables/tutorial";

export default {
  name: "TunnelView",
  components: {
    SearchBar,
    FiltersSection,
    GenericTable,
    GlassSelect,
    TunnelCsvExportDialog,
    PageError,
    DeleteModal,
    OrganizationTabs,
  },
  setup() {
    const tenant = useMultiTenant();
    const { start } = useTutorial();
    return { tenant, _start: start };
  },
  data() {
    return {
      tunnels: [],
      searchQuery: "",
      filteredTunnels: [],
      currentPage: 1,
      itemsPerPage: 10,
      showFilters: false,
      selectedFilters: {
        siteFilter: "",
        organizationFilter: "",
      },
      selectedTunnels: [],
      deletingItem: false,
      bulkAction: "",
      sites: {},
      dynamicFilters: [],
      showExportDialog: false,
      exportColumns: [
        { key: "name", label: "Name" },
        { key: "site", label: "Site" },
        { key: "org", label: "Organization" },
        { key: "created_date", label: "Created Date" },
        { key: "description", label: "Description" },
        { key: "tunnel_type", label: "Tunnel Type" },
        { key: "status", label: "Status" },
        { key: "remote_endpoint", label: "Remote Endpoint" },
        { key: "local_endpoint", label: "Local Endpoint" },
        { key: "port", label: "Port" },
      ],
      columns: [
        { key: "name", label: "Name", sortable: true, width: "25%" },
        { key: "site", label: "Site", sortable: true, width: "25%" },
        { key: "org", label: "Organisation", sortable: true, width: "25%" },
        {
          key: "created_date",
          label: "Created Date",
          sortable: true,
          width: "25%",
        },
      ],
      canAddTunnel: true,
      canDeleteTunnel: true,
      canViewTunnel: true,
      error: "",
      errorMessages: {
        400 : "Bad Request .The server could not understand the request.",
        401 : "Unauthorized access. Please check your authentication token or log in again.",
        403 : "You do not have permission to access this resource. Contact a staff member to assign you the permissions to access this page.",
        404 : "Page Not Found.",
        500 : "Internal Server Error - Something went wrong on the server.",
        422 : "Malformed request. Failed to fetch tunnels!"
      },
      isDeleting: false,
      tunnelTutorialSteps: [
        {
          element: '#searchbar',
          action: 'display',
          message: 'Search tunnels by name or use filters to narrow results.'
        },
        {
          element: '#add-tunnel-btn',
          action: 'display',
          message: 'Click here to create a new tunnel.'
        },
        {
          element: '#tunnels-table',
          action: 'display',
          message: 'Click any row to view or edit tunnel details.'
        },
      ],
    };
  },
  computed: {
    user() {
      return useStore.getUser();
    },
    isServiceProviderUser() {
      return useStore.isServiceProviderUser();
    }, 
    isFiltered() {
      return (
        this.searchQuery !== "" ||
        this.selectedFilters.siteFilter !== "" ||
        this.selectedFilters.organizationFilter !== ""
      );
    },
    totalPages() {
      return this.itemsPerPage === -1
        ? 1
        : Math.ceil(this.filteredTunnels.length / this.itemsPerPage);
    },
    paginatedTunnels() {
      if (this.itemsPerPage === -1) {
        return this.filteredTunnels;
      }
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredTunnels.slice(start, end);
    },
    selectedObjects(){
      return this.selectedTunnels.map(tunnel => ({
        name: "Tunnel",
        value: tunnel.name,
       }));
    },
    count(){
      return [{name: "Tunnels", value: this.selectedObjects.length}]
    }
  },
  watch: {
    searchQuery() {
      this.applyFilters();
    },
    user: {
      handler(user) {
        if (user) {
          this.checkPermissions();
        }
      },
      immediate: true
    },
  },
  async mounted() {
    this.checkPermissions();
    await this.tenant.fetchOrganizations();
    if (this.tenant.currentOrganizationId.value) {
      await this.tenant.syncUrlParam();
      await this.fetchTunnels(this.tenant.currentSchemaName.value);
    } else {
      await this.tenant.loadInitialData(this.fetchTunnels);
    }
    this.initializeFilters();
  },
  methods: {
    async startTutorial() {
      await this._start(this.tunnelTutorialSteps);
    },
    async checkPermissions(){
      if (this.user) {
        this.canAddTunnel = await useStore.hasPermission("Can add tunnel");
        this.canDeleteTunnel = await useStore.hasPermission("Can delete tunnel");
        this.canViewTunnel = await useStore.hasPermission("Can view tunnel");
      }
    },
    async handleOrgSelect(orgId) {
      try {
        this.error = "";
        await this.tenant.switchOrganization(orgId, this.fetchTunnels);
      } catch (error) {
        this.error = "Failed to switch organization";
        console.error('Organization select error:', error);
      }
    },
    goToAddTunnel() {
      this.$router.push({
        name: 'AddTunnel', 
        query: this.tenant.addSchemaParam()
      });
    },
    async fetchTunnels(schemaName = null) {
      try {
        if (!schemaName) {
          const validation  = this.tenant.validateSchemaForOperation();
          if (!validation.valid) {
            this.error = "Malformed request. Failed to fetch tunnels!";
            this.errorCode = "422!";
            return;
          }
          schemaName = validation.schemaName;
        }
        const tunnels = await getAllTunnels(schemaName);
        await this.fetchSites(schemaName);
        this.tunnels = [...tunnels];
        this.applyFilters();
      } catch (error) {
        if (error.response) {
          this.error = `${this.errorMessages[error.response.status]}`;
          this.errorCode = `${error.response.status}`;
        } else{
          this.error = `Unknown Error`
        }
      }
    },
    async fetchSites(schemaName = null) {
      try {
        if (!schemaName) {
          const validation = this.tenant.validateSchemaForOperation();
          if (!validation.valid) {
            this.error = "Malformed request. Failed to fetch sites for tunnels!";
            this.errorCode = "422!";
            return;
          }
          schemaName = validation.schemaName;
        }
        
        const sites = await getSites(schemaName);
        this.sites = sites.reduce((acc, site) => {
          acc[site.id] = site.name;
          return acc;
        }, {});
        this.initializeFilters();
      } catch (error) {
        console.error("Error fetching sites:", error);
      }
    },
    goToTunnelDetail(tunnelId) {
      this.$router.push({ name: "TunnelDetail", params: { id: tunnelId.id }, query: this.tenant.addSchemaParam() });
    },
    initializeFilters() {
      this.dynamicFilters = [
        {
          name: "siteFilter",
          label: "Site",
          options: [{ value: "", label: "All" }].concat(
            Object.entries(this.sites).map(([id, name]) => ({
              value: id,
              label: name,
            }))
          ),
        },
        {
          name: "organizationFilter",
          label: "Organization",
          options: [{ value: "", label: "All" }].concat(
            this.tenant.organizations.value.map((org) => ({
                value: String(org.id),
                label: org.name,
            }))
          ),
        },
      ];
    },
    applyFilters() {
      this.filteredTunnels = this.tunnels.filter((tunnel) => {
        const nameMatch = tunnel.name
          .toLowerCase()
          .includes(this.searchQuery.toLowerCase());

        const siteMatch =
          this.selectedFilters.siteFilter === "" ||
          tunnel.site.toString() === this.selectedFilters.siteFilter;

        const orgMatch =
          this.selectedFilters.organizationFilter === "" ||
          tunnel.org.toString() === this.selectedFilters.organizationFilter;

        return nameMatch && siteMatch && orgMatch;
      });

      this.currentPage = 1;
    },
    changePage(delta) {
      this.currentPage += delta;
    },
    updatePagination() {
      this.currentPage = 1;
    },
    updateSelectedTunnels(selectedItems) {
      this.selectedTunnels = selectedItems.filter((item => !item.marked_for_deletion));
    },
    getSiteName(siteId) {
      return this.sites[siteId] || "redacted";
    },
    getOrganizationName(orgId) {
      return this.organizations[orgId] || "redacted";
    },
    formatDateTime(dateString) {
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZoneName: "short",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    },
    async deleteSelectedTunnels() {
      if (this.selectedTunnels.length === 0) {
        alert("No tunnels selected for deletion");
        return;
      }

      try {
        const schemaName = this.tenant.currentSchemaName.value;
        if (!schemaName ) {
          this.error = "Malformed request. Failed to delete selected tunnels!";
          this.errorCode = "422!";
          return;
        }
        const deletePromises = this.selectedTunnels.map((tunnel) =>
          deleteTunnel(tunnel.id, schemaName)
        );
        await Promise.all(deletePromises);

        this.fetchTunnels()
        this.$refs.table.clearSelection();
        this.applyFilters();
        this.selectedTunnels = [];
      } catch (error) {
        console.error("Error during bulk delete:", error);
        alert("An error occurred while deleting tunnels. Please try again.");
      } finally {
        this.isDeleting = false;
      }
    },
    async applyBulkAction() {
      if (this.bulkAction === "delete" ) {
        this.deletingItem = true;
      }
      this.bulkAction = "";
    },
    async confirmDelete() {
      this.isDeleting = true;
      await this.deleteSelectedTunnels();
      this.deletingItem = false; 
      this.bulkAction = "";
    },
  },
};
</script>
